import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Briefcase,
  Save,
  Sparkles,
  Search,
  Loader2,
  CheckCircle2,
  MapPin,
  Flame,
  Droplet,
  Thermometer,
  Wrench,
  Hammer,
  Cpu,
  Home as HomeIcon,
  AlertTriangle,
  Snowflake,
  Cog,
  GitBranch,
  AlertCircle,
  Trash2,
  HeartPulse,
  Leaf,
  Box,
  Activity,
  Plus,
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useClientStore } from '../lib/store';
import ScheduleCheck from '../components/ScheduleCheck';
import ClientFormModal from '../components/ClientFormModal';

const WORKERS = ['Sammy', 'Jim', 'Stewart'];
const STATUSES = ['Pending', 'In Progress', 'Awaiting Parts', 'Ready to Invoice', 'Completed'];

// Map ionicon names (returned by backend) → lucide icon
const ICON_MAP: Record<string, any> = {
  flame: Flame,
  water: Droplet,
  'water-outline': Droplet,
  thermometer: Thermometer,
  build: Wrench,
  hammer: Hammer,
  'hardware-chip': Cpu,
  home: HomeIcon,
  warning: AlertTriangle,
  snow: Snowflake,
  cog: Cog,
  'git-branch': GitBranch,
  'alert-circle': AlertCircle,
  trash: Trash2,
  medical: HeartPulse,
  leaf: Leaf,
  cube: Box,
  pulse: Activity,
};

interface JobType {
  name: string;
  icon: string;
  description: string;
  typical_hours: number;
  keywords?: string[];
  labour_description_template?: string;
}

interface XeroSearchResult {
  contact_id: string;
  name: string;
  email?: string;
}

interface XeroContactDetail {
  contact_id: string;
  name: string;
  email?: string;
  phones?: string[];
  addresses?: string[];
}

export default function CreateJob() {
  const navigate = useNavigate();
  const { clients, fetchClients } = useClientStore();

  // Form state
  const [title, setTitle] = useState('');
  const [jobType, setJobType] = useState<JobType | null>(null);
  const [typeName, setTypeName] = useState('Plumbing');
  const [clientId, setClientId] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [time, setTime] = useState('09:00');
  const [durationType, setDurationType] = useState<'hours' | 'days'>('hours');
  const [durationHours, setDurationHours] = useState(1);
  const [durationDays, setDurationDays] = useState(1);
  const [assignedTo, setAssignedTo] = useState('Stewart');
  const [status, setStatus] = useState('Pending');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // AI-loaded job types
  const [jobTypes, setJobTypes] = useState<JobType[]>([]);
  const [jobTypesDetected, setJobTypesDetected] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(true);

  // Xero contact import
  const [clientMode, setClientMode] = useState<'existing' | 'xero'>('existing');
  const [xeroQ, setXeroQ] = useState('');
  const [xeroResults, setXeroResults] = useState<XeroSearchResult[]>([]);
  const [xeroSearching, setXeroSearching] = useState(false);
  const [xeroDetail, setXeroDetail] = useState<XeroContactDetail | null>(null);
  const [importingXero, setImportingXero] = useState(false);
  const [xeroConnected, setXeroConnected] = useState(false);

  // Manual create-client modal
  const [showNewClient, setShowNewClient] = useState(false);

  useEffect(() => {
    fetchClients();
    (async () => {
      try {
        const [typesRes, statusRes] = await Promise.all([
          api.get('/job-types'),
          api.get('/xero/status'),
        ]);
        setJobTypes(typesRes.data.job_types || []);
        setJobTypesDetected(typesRes.data.detected || false);
        setXeroConnected(statusRes.data.connected || false);
      } catch (e) {
        /* ignore */
      } finally {
        setLoadingTypes(false);
      }
    })();
  }, [fetchClients]);

  // Debounced Xero contact search
  useEffect(() => {
    if (clientMode !== 'xero' || !xeroConnected) {
      setXeroResults([]);
      return;
    }
    if (!xeroQ.trim() || xeroQ.trim().length < 2) {
      setXeroResults([]);
      return;
    }
    setXeroSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/xero/contacts', { params: { q: xeroQ.trim() } });
        setXeroResults(r.data.results || []);
      } catch (e) {
        setXeroResults([]);
      } finally {
        setXeroSearching(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [xeroQ, clientMode, xeroConnected]);

  const selectedClient = clients.find((c) => c.id === clientId);

  useEffect(() => {
    if (selectedClient && selectedClient.addresses.length > 0 && !clientAddress) {
      setClientAddress(selectedClient.addresses[0].address_line);
    }
  }, [selectedClient, clientAddress]);

  const pickJobType = (t: JobType) => {
    setJobType(t);
    setTypeName(t.name);
    // Auto-fill duration
    if (t.typical_hours) {
      if (t.typical_hours >= 8) {
        setDurationType('days');
        setDurationDays(Math.max(1, Math.round(t.typical_hours / 8)));
      } else {
        setDurationType('hours');
        setDurationHours(t.typical_hours);
      }
    }
    // Suggest title if empty
    if (!title) setTitle(t.name);
  };

  // AI title → job type suggestion (debounced)
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<any>(null);
  useEffect(() => {
    setAiSuggestion(null);
    if (!title.trim() || title.trim().length < 6 || jobTypes.length === 0) return;
    const t = setTimeout(async () => {
      setAiSuggesting(true);
      try {
        const res = await api.post('/ai/suggest-job-type', {
          title: title.trim(),
          description: notes.trim() || null,
        });
        if (res.data?.name) setAiSuggestion(res.data);
      } catch (e) {
        /* ignore */
      } finally {
        setAiSuggesting(false);
      }
    }, 900);
    return () => clearTimeout(t);
  }, [title, notes, jobTypes.length]);

  const acceptAiSuggestion = () => {
    if (!aiSuggestion) return;
    const match = jobTypes.find((t) => t.name === aiSuggestion.name);
    if (match) {
      pickJobType({ ...match, typical_hours: aiSuggestion.typical_hours || match.typical_hours });
    }
    setAiSuggestion(null);
  };

  const importXeroContact = async (contact: XeroSearchResult) => {
    setImportingXero(true);
    try {
      const detail = await api.get(`/xero/contacts/${contact.contact_id}`);
      const d: XeroContactDetail = detail.data;
      setXeroDetail(d);

      // Create local client with all pulled addresses
      const payload: any = {
        name: d.name,
        addresses: (d.addresses || []).map((a) => ({ address_line: a })),
        xero_customer_id: d.contact_id,
      };
      // Idempotent: check if we already have a client with this xero_customer_id
      const existing = clients.find((c) => c.xero_customer_id === d.contact_id);
      let newClientId: string;
      if (existing) {
        newClientId = existing.id;
        toast.success(`Using existing client “${d.name}”`);
      } else {
        const created = await api.post('/clients', payload);
        newClientId = created.data.id;
        toast.success(`Imported “${d.name}” with ${payload.addresses.length} address${payload.addresses.length === 1 ? '' : 'es'}`);
        await fetchClients();
      }
      setClientId(newClientId);
      // Pre-select first address
      if (d.addresses && d.addresses.length > 0) {
        setClientAddress(d.addresses[0]);
      }
      setClientMode('existing');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to import Xero contact');
    } finally {
      setImportingXero(false);
    }
  };

  const submit = async () => {
    if (!title.trim()) return toast.error('Please enter a title');
    if (!clientId) return toast.error('Please pick a client');
    if (!clientAddress) return toast.error('Please pick an address');

    setSaving(true);
    try {
      const payload: any = {
        title: title.trim(),
        type: typeName,
        client_id: clientId,
        client_address: clientAddress,
        location: { address: clientAddress },
        date,
        time,
        duration_type: durationType,
        assigned_to: assignedTo,
        status,
        notes: notes.trim() || null,
      };
      if (durationType === 'hours') payload.duration_hours = durationHours;
      else payload.duration_days = durationDays;

      const res = await api.post('/jobs', payload);
      toast.success(`Job ${res.data.job_number} created`);
      navigate(`/jobs/${res.data.id}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to create job');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-3"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-brand-100 grid place-items-center">
            <Briefcase className="h-5 w-5 text-brand-700" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Book a new job</h1>
        </div>
        <p className="text-slate-500">A job number will be generated automatically.</p>
      </div>

      <div className="card p-5 sm:p-6 space-y-6">
        {/* Job type picker */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Job type</div>
            {jobTypesDetected && (
              <span className="chip bg-purple-100 text-purple-800 ring-1 ring-inset ring-purple-200 text-[10px]">
                <Sparkles className="h-3 w-3" /> Learned from Xero
              </span>
            )}
          </div>
          {loadingTypes ? (
            <div className="h-20 rounded-lg bg-slate-100 animate-pulse" />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {jobTypes.map((t) => {
                const Icon = ICON_MAP[t.icon] || Wrench;
                const active = jobType?.name === t.name;
                return (
                  <button
                    key={t.name}
                    type="button"
                    onClick={() => pickJobType(t)}
                    className={`text-left rounded-xl p-3 border-2 transition ${
                      active
                        ? 'border-brand-600 bg-brand-50 ring-4 ring-brand-100'
                        : 'border-slate-200 hover:border-brand-300 hover:bg-brand-50/30'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`h-4 w-4 ${active ? 'text-brand-700' : 'text-slate-500'}`} />
                      <span className="text-xs font-bold text-slate-400 tabular-nums">
                        ≈{t.typical_hours}h
                      </span>
                    </div>
                    <div className={`text-sm font-semibold ${active ? 'text-brand-800' : 'text-slate-900'} line-clamp-1`}>
                      {t.name}
                    </div>
                    {t.description && (
                      <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{t.description}</div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Title */}
        <Field label="Job title">
          <input
            className="input"
            placeholder="e.g. Kitchen tap leak repair"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          {(aiSuggesting || aiSuggestion) && (
            <div className="mt-2">
              {aiSuggesting && !aiSuggestion ? (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Loader2 className="h-3 w-3 animate-spin" /> AI thinking…
                </div>
              ) : aiSuggestion ? (
                <div className="flex items-center gap-2 text-xs bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                  <Sparkles className="h-3.5 w-3.5 text-purple-600 shrink-0" />
                  <span className="text-purple-800">
                    AI suggests <strong>{aiSuggestion.name}</strong> · ~{aiSuggestion.typical_hours}h
                    {aiSuggestion.confidence ? ` · ${Math.round(aiSuggestion.confidence * 100)}%` : ''}
                  </span>
                  <button
                    type="button"
                    onClick={acceptAiSuggestion}
                    className="ml-auto text-xs font-bold text-purple-700 hover:text-purple-900"
                  >
                    Accept →
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </Field>

        {/* Client */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Client</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowNewClient(true)}
                className="text-xs font-semibold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1"
              >
                <Plus className="h-3 w-3" /> New client
              </button>
              <div className="flex text-xs rounded-lg bg-slate-100 p-0.5">
                <button
                  type="button"
                  onClick={() => setClientMode('existing')}
                  className={`px-3 py-1 rounded-md font-semibold transition ${
                    clientMode === 'existing' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'
                  }`}
                >
                  Existing
                </button>
                <button
                  type="button"
                  onClick={() => setClientMode('xero')}
                  disabled={!xeroConnected}
                  title={!xeroConnected ? 'Connect Xero to enable this' : ''}
                  className={`px-3 py-1 rounded-md font-semibold transition inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed ${
                    clientMode === 'xero' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'
                  }`}
                >
                  <Sparkles className="h-3 w-3" /> From Xero
                </button>
              </div>
            </div>
          </div>

          {clientMode === 'existing' ? (
            <select
              className="input"
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                setClientAddress('');
              }}
            >
              <option value="">Select client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.xero_customer_id ? '  ✓ Xero' : ''}
                </option>
              ))}
            </select>
          ) : (
            <div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  className="input pl-9"
                  placeholder="Search Xero contacts by name…"
                  value={xeroQ}
                  onChange={(e) => setXeroQ(e.target.value)}
                />
                {xeroSearching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
                )}
              </div>

              {xeroResults.length > 0 && (
                <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                  {xeroResults.map((r) => (
                    <button
                      key={r.contact_id}
                      type="button"
                      onClick={() => importXeroContact(r)}
                      disabled={importingXero}
                      className="w-full text-left px-3 py-2 hover:bg-brand-50 transition flex items-center gap-2 disabled:opacity-60"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-slate-900">{r.name}</div>
                        {r.email && <div className="text-xs text-slate-500">{r.email}</div>}
                      </div>
                      {importingXero ? (
                        <Loader2 className="h-4 w-4 animate-spin text-brand-600" />
                      ) : (
                        <span className="chip bg-brand-50 text-brand-700 text-[10px]">
                          Import
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {xeroQ && !xeroSearching && xeroResults.length === 0 && xeroQ.length >= 2 && (
                <div className="mt-2 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                  No Xero contacts match “{xeroQ}”.
                </div>
              )}

              {!xeroConnected && (
                <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Xero is not connected. Connect from Settings first.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Address */}
        {selectedClient && selectedClient.addresses.length > 0 && (
          <Field label={`Address (${selectedClient.addresses.length} on file)`}>
            {selectedClient.addresses.length === 1 ? (
              <div className="input flex items-center gap-2 bg-slate-50 text-slate-700">
                <MapPin className="h-4 w-4 text-slate-400 shrink-0" />
                <span className="truncate">{selectedClient.addresses[0].address_line}</span>
              </div>
            ) : (
              <select
                className="input"
                value={clientAddress}
                onChange={(e) => setClientAddress(e.target.value)}
              >
                {selectedClient.addresses.map((a, i) => (
                  <option key={i} value={a.address_line}>
                    {a.address_line}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}

        {selectedClient && selectedClient.addresses.length === 0 && (
          <Field label="Address (add manually — none on file)">
            <input
              className="input"
              placeholder="e.g. 12 High Street, Glasgow"
              value={clientAddress}
              onChange={(e) => setClientAddress(e.target.value)}
            />
          </Field>
        )}

        {selectedClient?.xero_customer_id && (
          <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 -mt-2">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Linked to Xero contact — invoices will use this contact.
          </div>
        )}

        {/* Scheduling */}
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Date">
            <input
              type="date"
              className="input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
          <Field label="Start time">
            <input
              type="time"
              className="input"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </Field>
        </div>

        <div className="grid sm:grid-cols-3 gap-5">
          <Field label="Duration in">
            <select
              className="input"
              value={durationType}
              onChange={(e) => setDurationType(e.target.value as 'hours' | 'days')}
            >
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
          </Field>
          {durationType === 'hours' ? (
            <Field label="Hours">
              <input
                type="number"
                min={0.5}
                step={0.5}
                className="input"
                value={durationHours}
                onChange={(e) => setDurationHours(Number(e.target.value))}
              />
            </Field>
          ) : (
            <Field label="Days">
              <input
                type="number"
                min={1}
                step={1}
                className="input"
                value={durationDays}
                onChange={(e) => setDurationDays(Number(e.target.value))}
              />
            </Field>
          )}
          <Field label="Assigned to">
            <select
              className="input"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
            >
              {WORKERS.map((w) => (
                <option key={w}>{w}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Status">
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </Field>

        {/* Schedule check */}
        <ScheduleCheck
          date={date}
          time={time}
          durationType={durationType}
          durationHours={durationHours}
          durationDays={durationDays}
          assignedTo={assignedTo}
          onAssignChange={(w) => setAssignedTo(w)}
        />

        <Field label="Notes (optional)">
          <textarea
            className="input min-h-[100px] resize-y"
            placeholder="Any additional context, materials needed, etc."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>

        <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
          <button onClick={() => navigate(-1)} className="btn btn-secondary" disabled={saving}>
            Cancel
          </button>
          <button onClick={submit} className="btn btn-primary" disabled={saving}>
            <Save className="h-4 w-4" />
            {saving ? 'Creating…' : 'Create job'}
          </button>
        </div>
      </div>

      {showNewClient && (
        <ClientFormModal
          onClose={() => setShowNewClient(false)}
          onSaved={async (created) => {
            // refresh clients list, then auto-select the new one
            await fetchClients();
            if (created?.id) {
              setClientId(created.id);
              if (created.addresses && created.addresses.length > 0) {
                setClientAddress(created.addresses[0].address_line);
              }
              setClientMode('existing');
            }
          }}
        />
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
        {label}
      </div>
      {children}
    </label>
  );
}
